import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { visitInstructionsApi, instructionsKey } from '../../api/visit-instructions.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, LoadingBlock } from '../ui.tsx';
import type { OfficeVisitInstruction } from '../../../shared/visit-instructions.ts';

export function VisitInstructionsCard({ appointmentId, closed = false }: { appointmentId: string; closed?: boolean }) {
  const { user, company, currentLocation, impersonatedBy } = useSession();
  const scope = JSON.stringify(instructionsKey(appointmentId, user, company, currentLocation?.id, impersonatedBy));
  return <VisitInstructionsForm key={scope} appointmentId={appointmentId} closed={closed} />;
}

function VisitInstructionsForm({ appointmentId, closed }: { appointmentId: string; closed: boolean }) {
  const { t } = useTranslation();
  const { can, user, company, currentLocation, impersonatedBy } = useSession();
  const cache = useQueryClient();
  const key = instructionsKey(appointmentId, user, company, currentLocation?.id, impersonatedBy);
  const read = useQuery({
    queryKey: key,
    queryFn: () => visitInstructionsApi.read(appointmentId),
    refetchInterval: 30_000,
  });
  const refresh = () => cache.invalidateQueries({ queryKey: key });
  const acknowledge = useMutation({
    mutationFn: (version: number) => visitInstructionsApi.acknowledge(appointmentId, version),
    onSettled: refresh,
  });
  const [text, setText] = useState('');
  const retry = useRef<string | null>(null);
  const add = useMutation({
    mutationFn: () => {
      retry.current ??= crypto.randomUUID();
      return visitInstructionsApi.add(appointmentId, { text, requestKey: retry.current });
    },
    onSuccess: async () => {
      setText('');
      retry.current = null;
      await refresh();
    },
  });
  return (
    <section className="mb-4 min-w-0 rounded-lg border-2 border-[color:var(--color-warning)] bg-[color:var(--color-surface)] p-4">
      <h2 className="text-lg font-bold">{t('visitInstructions.todayOnly')}</h2>
      <p className="mb-3 text-sm">{t('visitInstructions.hint')}</p>
      {read.isLoading && <LoadingBlock label={t('common.loading')} />}
      {read.isError && (
        <Alert tone="error">
          <p>{t('visitInstructions.loadError')}</p>
          <Button variant="secondary" onClick={() => void read.refetch()}>
            {t('visitInstructions.retry')}
          </Button>
        </Alert>
      )}
      {read.data && (
        <>
          {read.data.items.length === 0 && <p>{t('visitInstructions.empty')}</p>}
          <ol className="space-y-3">
            {read.data.items.map((item) => (
              <li key={item.id} className="min-w-0">
                <time className="text-xs" dateTime={item.createdAt}>
                  {new Date(item.createdAt).toLocaleString()}
                </time>
                <p className="break-words whitespace-pre-wrap">{item.text}</p>
              </li>
            ))}
          </ol>
          {read.data.canAcknowledge && read.data.version > 0 && !closed && (
            <div className="mt-3 print:hidden">
              {read.data.acknowledged ? (
                <p>{t('visitInstructions.acknowledged')}</p>
              ) : (
                <Button busy={acknowledge.isPending} onClick={() => acknowledge.mutate(read.data!.version)}>
                  {t('visitInstructions.gotIt')}
                </Button>
              )}
              {acknowledge.isError && <Alert tone="error">{t('visitInstructions.changed')}</Alert>}
            </div>
          )}
          {read.data.assignees && (
            <ul className="mt-3 text-sm print:hidden">
              {read.data.assignees.map((person) => (
                <li key={person.userId}>
                  {person.name}:{' '}
                  {person.acknowledgedAt ? t('visitInstructions.acknowledged') : t('visitInstructions.waiting')}
                </li>
              ))}
            </ul>
          )}
          {read.data.officeItems
            ?.filter((item) => item.status === 'pending')
            .map((item) => (
              <PendingInstruction
                key={item.id}
                appointmentId={appointmentId}
                item={item}
                closed={closed}
                onChanged={refresh}
              />
            ))}
          {read.data.officeItems
            ?.filter((item) => item.messageId && item.conversationId)
            .map((item) => (
              <p key={`source-${item.id}`} className="mt-2 print:hidden">
                <Link className="underline" to={`/messages?conversation=${item.conversationId}`}>
                  {t('visitInstructions.source')}
                </Link>
              </p>
            ))}
        </>
      )}
      {can('schedule:write') && !closed && (
        <form
          className="mt-4 print:hidden"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <label className="field-label" htmlFor={`instruction-${appointmentId}`}>
            {t('visitInstructions.addText')}
          </label>
          <textarea
            className="field-control"
            id={`instruction-${appointmentId}`}
            value={text}
            maxLength={5000}
            rows={3}
            disabled={add.isPending}
            onChange={(e) => {
              setText(e.target.value);
              retry.current = null;
            }}
          />
          <Button className="mt-2" type="submit" busy={add.isPending} disabled={!text.trim() || !read.data}>
            {t('visitInstructions.add')}
          </Button>
          {add.isError && <Alert tone="error">{t('visitInstructions.saveError')}</Alert>}
        </form>
      )}
    </section>
  );
}

function PendingInstruction({
  appointmentId,
  item,
  closed,
  onChanged,
}: {
  appointmentId: string;
  item: OfficeVisitInstruction;
  closed: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(item.text);
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      visitInstructionsApi.decide(
        appointmentId,
        item.id,
        approve ? { decision: 'approve', text } : { decision: 'decline' },
      ),
    onSettled: onChanged,
  });
  return (
    <div className="mt-4 rounded border p-3 print:hidden">
      <h3 className="font-semibold">{t('visitInstructions.pending')}</h3>
      <p className="mb-2 text-sm">{t('visitInstructions.approvalHint')}</p>
      <label className="field-label" htmlFor={`pending-${item.id}`}>
        {t('visitInstructions.selectedText')}
      </label>
      <textarea
        className="field-control"
        id={`pending-${item.id}`}
        rows={3}
        value={text}
        maxLength={5000}
        disabled={decide.isPending || closed}
        onChange={(e) => setText(e.target.value)}
      />
      {!closed && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button busy={decide.isPending} disabled={!text.trim()} onClick={() => decide.mutate(true)}>
            {t('visitInstructions.approve')}
          </Button>
          <Button variant="secondary" disabled={decide.isPending} onClick={() => decide.mutate(false)}>
            {t('visitInstructions.decline')}
          </Button>
        </div>
      )}
      {decide.isError && <Alert tone="error">{t('visitInstructions.decisionError')}</Alert>}
    </div>
  );
}
